import { memo, useEffect, useMemo, useState, type CSSProperties } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  HomeAddressIcon,
  Layout,
  SupportIcon,
  Title,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { BodyCopy } from "../components/BodyCopy";
import { FloatingField } from "../components/FloatingField";
import { Navbar } from "../components/Navbar";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { COUNTRIES } from "../constants/countries";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useStore } from "../store/globalState";
import { useRequestStore } from "../store/useRequestStore";
import { useSessionStore } from "../store/useSessionStore";
import {
  generateFundRequestLink,
  type FundRequestPayoutInput,
  type FundRequestPayoutMethod,
} from "../api/fund-request";
import { getWidgetApiError, isDeadLinkError } from "../services/api";
import { getListStub, setListStub, type ListStub } from "../utils/listStub";
import { clearPhoneSkip } from "../utils/phoneGate";
import {
  loadZipLookup,
  zipLookupKey,
  type ZipLookup,
} from "../utils/zip-lookup";

type ScreeningField = "dateOfBirth" | "address";

interface FormState {
  dateOfBirth: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  countryISO2: string;
}

const payoutForMint = (
  payoutMethod: FundRequestPayoutMethod,
  payout: FundRequestPayoutInput,
): FundRequestPayoutInput => {
  if (payout.paymentMethodId) {
    if (payoutMethod === "BANK_ACCOUNT") {
      return {
        paymentMethodId: payout.paymentMethodId,
        ...(payout.bankId ? { bankId: payout.bankId } : {}),
      };
    }
    return {
      paymentMethodId: payout.paymentMethodId,
      brand: payout.brand,
      last4: payout.last4,
      cardExpiry: payout.cardExpiry,
      cardholderName: payout.cardholderName,
    };
  }
  const wirePayout = { ...payout };
  delete wirePayout.source;
  delete wirePayout.cvsValidationId;
  delete wirePayout.numberToken;
  delete wirePayout.validatedFor;
  return wirePayout;
};

export const RequestScreeningPage = memo(() => {
  const { t } = useTranslation();
  const location = useLocation();
  const { redirectTo } = useRouteWithToken();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { draft, setMintResult } = useRequestStore();
  const { openSupport, supportSheet } = useSupportSheet();
  const colorConfig = theme.whiteView;
  const { bodyCopy, dropzoneColors, backgroundBtn, textColorBtn } = colorConfig;
  const fieldColors = {
    background: colorConfig.background,
    title: colorConfig.title,
    bodyCopy,
    inactiveColor: colorConfig.subtitile,
    errorColor: colorConfig.errorColor,
  };
  const errorStyle: CSSProperties = {
    color: colorConfig.errorColor,
    fontSize: 13,
  };

  const fromState = (location.state as { requiredFields?: ScreeningField[] } | null)
    ?.requiredFields;
  const fromSession = session.data?.config?.actionRequired?.requiredFields;
  const requiredFields = fromState?.length
    ? fromState
    : fromSession?.length
      ? fromSession
      : (["dateOfBirth", "address"] as ScreeningField[]);
  const needDob = requiredFields.includes("dateOfBirth");
  const needAddress = requiredFields.includes("address");

  const [form, setForm] = useState<FormState>({
    dateOfBirth: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    zip: "",
    countryISO2: session.data?.user?.country ?? "",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [stub, setStub] = useState<ListStub | null>(() => getListStub());
  const [zipLookup, setZipLookup] = useState<ZipLookup | null>(null);

  const isUS = form.countryISO2 === "US";

  useEffect(() => {
    if (!isUS) return;
    let active = true;
    loadZipLookup()
      .then((lookup) => {
        if (active) setZipLookup(() => lookup);
      })
      .catch(() => {
        // Keep the submit disabled until city and state can be resolved.
      });
    return () => {
      active = false;
    };
  }, [isUS]);

  const zipLocation = useMemo(() => {
    if (!isUS || !zipLookup) return null;
    const key = zipLookupKey(form.zip);
    return key ? zipLookup(key) : null;
  }, [form.zip, isUS, zipLookup]);
  const zipUnknown =
    isUS && Boolean(zipLookup) && Boolean(zipLookupKey(form.zip)) && !zipLocation;
  const usAddressReady = Boolean(form.line1.trim()) && Boolean(zipLocation);

  const setField = (key: keyof FormState, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    if (errors[key]) {
      setErrors((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
    }
    setFormError(null);
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (needDob && !form.dateOfBirth) {
      next.dateOfBirth = t("requesterScreening.dateOfBirthRequired");
    }
    if (needAddress) {
      if (!form.line1.trim()) next.line1 = t("requesterScreening.streetRequired");
      if (isUS) {
        if (!/^\d{5}(-\d{4})?$/.test(form.zip.trim())) {
          next.zip = t("requesterScreening.zipRequired");
        } else if (!zipLocation) {
          next.zip = t("requesterScreening.zipNotFound");
        }
      } else {
        if (!form.city.trim()) next.city = t("requesterScreening.cityRequired");
        if (!form.state.trim()) next.state = t("requesterScreening.stateRequired");
        if (!form.zip.trim()) next.zip = t("requesterScreening.zipRequired");
      }
      if (!form.countryISO2.trim()) {
        next.countryISO2 = t("requesterScreening.countryRequired");
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async () => {
    if (submitting) return;
    setFormError(null);
    if (!draft || !session.data?.linkId) {
      redirectTo(APP_ROUTES.TRANSFER_PAGE);
      return;
    }
    if (!validate()) return;
    setSubmitting(true);
    try {
      const result = await generateFundRequestLink(session.data.linkId, {
        sourceCountry: draft.sourceCountry,
        destinationCountry: draft.destinationCountry,
        sourceCurrency: draft.sourceCurrency,
        destinationCurrency: draft.destinationCurrency,
        amount: draft.amount,
        payoutMethod: draft.payoutMethod,
        payout: payoutForMint(draft.payoutMethod, draft.payout),
        ...(draft.capabilityKey ? { capabilityKey: draft.capabilityKey } : {}),
        ...(needDob ? { dateOfBirth: form.dateOfBirth } : {}),
        ...(needAddress
          ? {
              address: {
                line1: form.line1.trim(),
                ...(form.line2.trim() ? { line2: form.line2.trim() } : {}),
                city: (isUS ? zipLocation?.city : form.city.trim()) || "",
                state: (isUS ? zipLocation?.stateCode : form.state.trim()) || "",
                zip: form.zip.trim(),
                countryISO2: form.countryISO2.trim().toUpperCase(),
              },
            }
          : {}),
      });
      if (result.kind === "created") {
        clearPhoneSkip(session.data.linkId);
        setMintResult(result);
        redirectTo(APP_ROUTES.REQUEST_READY_PAGE);
        return;
      }
      if (result.kind === "review") {
        redirectTo(APP_ROUTES.REQUEST_REVIEW_PAGE);
        return;
      }
      setFormError(t("requesterScreening.stillRequired"));
    } catch (error) {
      if (isDeadLinkError(error)) return;
      const code = getWidgetApiError(error)?.code;
      setFormError(
        code === "RECIPIENT_SCREENING_UNAVAILABLE"
          ? t("requesterScreening.unavailable")
          : t("requesterScreening.tryAgain"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const chooseStub = (value: ListStub) => {
    setListStub(value);
    setStub(value);
  };

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="scrollable need-help-header">
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() => redirectTo(APP_ROUTES.TRANSFER_PAGE)}
            onIconClick={() => openSupport()}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="transfer-section">
          <ScrollFadeIndicator color={bodyCopy} />
          <Title
            title={t("requesterScreening.title")}
            align="left"
            color={TITLE_COLOR}
          />
          <BodyCopy textColor={bodyCopy} className="ofac-disambiguation-helper">
            {t("requesterScreening.helper")}
          </BodyCopy>
          {stub && (
            <section
              className="ofac-mock-controls"
              aria-label={t("requesterScreening.stubLabel")}
            >
              <BodyCopy textColor={bodyCopy}>
                {t("requesterScreening.stubLabel")}: <b>{stub}</b>
              </BodyCopy>
              <div className="ofac-mock-buttons">
                {(
                  [
                    ["form", "stubClear"],
                    ["block", "stubReview"],
                    ["down", "stubDown"],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={`ofac-mock-btn ${stub === value ? "active" : ""}`}
                    onClick={() => chooseStub(value)}
                  >
                    {t(`requesterScreening.${label}`)}
                  </button>
                ))}
              </div>
            </section>
          )}
          <form
            id="ofac-disambiguation-form"
            className="mock-card-form"
            autoComplete="on"
            onSubmit={(event) => {
              event.preventDefault();
              void onSubmit();
            }}
          >
            <section className="mock-card-fields">
              {needDob && (
                <div className="mock-card-field">
                  <FloatingField
                    label={t("requesterScreening.dateOfBirth")}
                    value={form.dateOfBirth || " "}
                    required
                    isValid={!errors.dateOfBirth}
                    colors={fieldColors}
                  >
                    <input
                      id="ofac-dob"
                      style={{ color: bodyCopy }}
                      type="date"
                      name="dateOfBirth"
                      autoComplete="bday"
                      value={form.dateOfBirth}
                      onChange={(event) =>
                        setField("dateOfBirth", event.target.value)
                      }
                    />
                  </FloatingField>
                  {errors.dateOfBirth && (
                    <div style={errorStyle}>{errors.dateOfBirth}</div>
                  )}
                </div>
              )}
              {needAddress && (
                <>
                  <div className="mock-card-field">
                    <FloatingField
                      label={t("requesterScreening.street")}
                      value={form.line1}
                      required
                      isValid={!errors.line1}
                      colors={fieldColors}
                      icon={
                        <HomeAddressIcon colors={dropzoneColors} size={18} />
                      }
                    >
                      <input
                        style={{ color: bodyCopy }}
                        name="line1"
                        autoComplete="address-line1"
                        value={form.line1}
                        onChange={(event) =>
                          setField("line1", event.target.value)
                        }
                      />
                    </FloatingField>
                    {errors.line1 && (
                      <div style={errorStyle}>{errors.line1}</div>
                    )}
                  </div>
                  <div className="mock-card-field">
                    <FloatingField
                      label={t("requesterScreening.street2")}
                      value={form.line2}
                      colors={fieldColors}
                    >
                      <input
                        style={{ color: bodyCopy }}
                        name="line2"
                        autoComplete="address-line2"
                        value={form.line2}
                        onChange={(event) =>
                          setField("line2", event.target.value)
                        }
                      />
                    </FloatingField>
                  </div>
                  {!isUS && (
                    <>
                      <div className="mock-card-field">
                        <FloatingField
                          label={t("requesterScreening.city")}
                          value={form.city}
                          required
                          isValid={!errors.city}
                          colors={fieldColors}
                        >
                          <input
                            style={{ color: bodyCopy }}
                            name="city"
                            autoComplete="address-level2"
                            value={form.city}
                            onChange={(event) =>
                              setField("city", event.target.value)
                            }
                          />
                        </FloatingField>
                        {errors.city && (
                          <div style={errorStyle}>{errors.city}</div>
                        )}
                      </div>
                      <div className="mock-card-field">
                        <FloatingField
                          label={t("requesterScreening.state")}
                          value={form.state}
                          required
                          isValid={!errors.state}
                          colors={fieldColors}
                        >
                          <input
                            style={{ color: bodyCopy }}
                            name="state"
                            autoComplete="address-level1"
                            value={form.state}
                            onChange={(event) =>
                              setField("state", event.target.value)
                            }
                          />
                        </FloatingField>
                        {errors.state && (
                          <div style={errorStyle}>{errors.state}</div>
                        )}
                      </div>
                    </>
                  )}
                  <div className="mock-card-field">
                    <FloatingField
                      label={t("requesterScreening.zip")}
                      value={form.zip}
                      required
                      isValid={!errors.zip}
                      colors={fieldColors}
                    >
                      <input
                        style={{ color: bodyCopy }}
                        name="zip"
                        autoComplete="postal-code"
                        inputMode={isUS ? "numeric" : "text"}
                        value={form.zip}
                        onChange={(event) =>
                          setField(
                            "zip",
                            isUS
                              ? event.target.value.replace(/[^\d-]/g, "")
                              : event.target.value,
                          )
                        }
                      />
                    </FloatingField>
                    {(errors.zip || zipUnknown) && (
                      <div style={errorStyle}>
                        {errors.zip ?? t("requesterScreening.zipNotFound")}
                      </div>
                    )}
                  </div>
                  <div className="mock-card-field">
                    <FloatingField
                      label={t("requesterScreening.country")}
                      value={form.countryISO2}
                      required
                      isValid={!errors.countryISO2}
                      colors={fieldColors}
                    >
                      <select
                        style={{ color: bodyCopy }}
                        name="country"
                        value={form.countryISO2}
                        onChange={(event) => {
                          setField("countryISO2", event.target.value);
                          setField("state", "");
                          setField("city", "");
                        }}
                      >
                        <option value="" />
                        {COUNTRIES.map((item) => (
                          <option key={item.code} value={item.code}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </FloatingField>
                    {errors.countryISO2 && (
                      <div style={errorStyle}>{errors.countryISO2}</div>
                    )}
                  </div>
                </>
              )}
              {formError && (
                <BodyCopy textColor={colorConfig.errorColor}>{formError}</BodyCopy>
              )}
            </section>
          </form>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              disabled={
                submitting || (needAddress && isUS && !usAddressReady)
              }
              onClick={() => void onSubmit()}
            >
              {submitting
                ? t("requesterScreening.submitting")
                : t("requesterScreening.submit")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
      {supportSheet}
    </>
  );
});
